/**
 * Karma Core - Provider Capability Matrix
 * 
 * Defines capabilities, latency, cost, and reliability for each provider
 */

import type { ProviderCapability, Modality } from "./types";

/**
 * Provider Capability Matrix
 * 
 * This matrix defines what each provider can do, how fast it is,
 * how much it costs, and how reliable it is.
 */
export const PROVIDER_CAPABILITIES: Record<string, ProviderCapability> = {
  anthropic: {
    provider: "anthropic",
    modalities: ["text", "embedding"],
    maxContext: 200000,
    functionCalling: true,
    streaming: true,
    averageLatency: 1200,
    costPer1kTokens: {
      input: 0.003,
      output: 0.015,
    },
    reliabilityScore: 0.95,
    knownFailureModes: ["rate_limit", "context_overflow"],
    models: ["claude-opus-4-6", "claude-3-5-sonnet-20241022", "claude-3-opus-20240229", "claude-3-sonnet-20240229"],
  },
  openai: {
    provider: "openai",
    modalities: ["text", "image", "embedding"],
    maxContext: 128000,
    functionCalling: true,
    streaming: true,
    averageLatency: 800,
    costPer1kTokens: {
      input: 0.0025,
      output: 0.01,
    },
    reliabilityScore: 0.92,
    knownFailureModes: ["rate_limit", "content_filter"],
    models: ["gpt-4-turbo-preview", "gpt-4", "gpt-3.5-turbo"],
  },
  xai: {
    provider: "xai",
    modalities: ["text"],
    maxContext: 131072,
    functionCalling: true,
    streaming: true,
    averageLatency: 400, // Fastest, most lifelike
    costPer1kTokens: {
      input: 0.001,
      output: 0.002,
    },
    reliabilityScore: 0.88,
    knownFailureModes: ["rate_limit"],
    models: ["grok-2-latest", "grok-beta"],
  },
  gemini: {
    provider: "gemini",
    modalities: ["text", "image", "embedding"],
    maxContext: 1000000,
    functionCalling: true,
    streaming: true,
    averageLatency: 1000,
    costPer1kTokens: {
      input: 0.00025,
      output: 0.001,
    },
    reliabilityScore: 0.90,
    knownFailureModes: ["safety_filter", "rate_limit"],
    models: ["gemini-2.0-flash-exp", "gemini-1.5-pro", "gemini-1.5-flash"],
  },
  mistral: {
    provider: "mistral",
    modalities: ["text", "embedding"],
    maxContext: 32000,
    functionCalling: true,
    streaming: true,
    averageLatency: 600,
    costPer1kTokens: {
      input: 0.0002,
      output: 0.0006,
    },
    reliabilityScore: 0.87,
    knownFailureModes: ["rate_limit"],
    models: ["mistral-large-latest", "mistral-medium-latest"],
  },
  cohere: {
    provider: "cohere",
    modalities: ["text", "embedding"],
    maxContext: 4096,
    functionCalling: false,
    streaming: true,
    averageLatency: 500,
    costPer1kTokens: {
      input: 0.00015,
      output: 0.0006,
    },
    reliabilityScore: 0.85,
    knownFailureModes: ["rate_limit"],
    models: ["command-r-plus", "command-r"],
  },
  fireworks: {
    provider: "fireworks",
    modalities: ["text"],
    maxContext: 32768,
    functionCalling: true,
    streaming: true,
    averageLatency: 300,
    costPer1kTokens: {
      input: 0.0001,
      output: 0.0003,
    },
    reliabilityScore: 0.83,
    knownFailureModes: ["rate_limit"],
    models: ["accounts/fireworks/models/llama-v3-70b-instruct"],
  },
  perplexity: {
    provider: "perplexity",
    modalities: ["text"], // Has web search capability
    maxContext: 131072,
    functionCalling: false,
    streaming: true,
    averageLatency: 2000, // Slower due to web search
    costPer1kTokens: {
      input: 0.001,
      output: 0.001,
    },
    reliabilityScore: 0.80,
    knownFailureModes: ["rate_limit", "search_timeout"],
    models: ["llama-3.1-sonar-large-128k-online"],
  },
  together: {
    provider: "together",
    modalities: ["text"],
    maxContext: 32768,
    functionCalling: false,
    streaming: true,
    averageLatency: 700,
    costPer1kTokens: {
      input: 0.00008,
      output: 0.00024,
    },
    reliabilityScore: 0.82,
    knownFailureModes: ["rate_limit"],
    models: ["meta-llama/Llama-3-70b-chat-hf"],
  },
  replicate: {
    provider: "replicate",
    modalities: ["image", "video"],
    maxContext: 0,
    functionCalling: false,
    streaming: false,
    averageLatency: 5000, // Image/video generation is slower
    costPer1kTokens: {
      input: 0,
      output: 0,
    },
    reliabilityScore: 0.75,
    knownFailureModes: ["generation_timeout", "rate_limit"],
    models: ["stability-ai/stable-diffusion", "anotherjesse/zeroscope-v2-xl"],
  },
  stability: {
    provider: "stability",
    modalities: ["image"],
    maxContext: 0,
    functionCalling: false,
    streaming: false,
    averageLatency: 3000,
    costPer1kTokens: {
      input: 0,
      output: 0,
    },
    reliabilityScore: 0.78,
    knownFailureModes: ["generation_timeout", "rate_limit"],
    models: ["stable-diffusion-xl-1024-v1-0"],
  },
  huggingface: {
    provider: "huggingface",
    modalities: ["text", "embedding"],
    maxContext: 8192,
    functionCalling: false,
    streaming: false,
    averageLatency: 1500,
    costPer1kTokens: {
      input: 0,
      output: 0,
    },
    reliabilityScore: 0.70,
    knownFailureModes: ["rate_limit", "model_unavailable"],
    models: ["meta-llama/Llama-3-8b-Instruct"],
  },
  bedrock: {
    provider: "bedrock",
    modalities: ["text", "embedding"],
    maxContext: 200000,
    functionCalling: true,
    streaming: true,
    averageLatency: 1100,
    costPer1kTokens: {
      input: 0.002,
      output: 0.008,
    },
    reliabilityScore: 0.89,
    knownFailureModes: ["rate_limit", "region_unavailable"],
    models: ["anthropic.claude-3-5-sonnet-20241022-v2:0"],
  },
};

/**
 * Get capability for a provider
 */
export function getProviderCapability(provider: string): ProviderCapability | null {
  return PROVIDER_CAPABILITIES[provider] || null;
}

/**
 * Get all providers that support a modality
 */
export function getProvidersForModality(modality: Modality): string[] {
  return Object.entries(PROVIDER_CAPABILITIES)
    .filter(([_, cap]) => cap.modalities.includes(modality))
    .map(([provider]) => provider);
}

/**
 * Check if provider supports function calling
 */
export function supportsFunctionCalling(provider: string): boolean {
  const cap = getProviderCapability(provider);
  return cap?.functionCalling || false;
}
